/**
 * Module: Book
 * Parent Block(s):
 * - list
 */

/**
 * External dependencies
 */

import isEmpty from 'lodash/isEmpty';

/**
 * DWT dependencies
 */

import { authorShapeIcon } from '../../img/icons';
import { getBlockAttrs, getBlockParentsByName } from '../utils/block-utils';

/**
 * DWT components
 */

import Placeholder from '../block-components/Placeholder';

/**
 * WP dependencies
 */

import apiFetch from '@wordpress/api-fetch';

import {
  InspectorControls,
  MediaUpload,
  MediaUploadCheck,
  useBlockProps
} from '@wordpress/block-editor';

import {
  Button,
  PanelBody,
  TextControl
} from '@wordpress/components';

import { useEffect, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

const Edit = ({ attributes, setAttributes, clientId }) => {
  const {
    hasAuthorPhoto,
    apiImage,
    authorId,
    authorRoles,
    display,
    firstInitial,
    lastInitial,
    spotlight,
    photocredit,
    photoDate,
    firstWorkCover,
    showMetadata,
    allowedContributorRoles,
    uploadedImage,
    altTextManual
  } = attributes;

  const listBlock = getBlockParentsByName( clientId, 'dwt/product-list' );
  const parentBlockAttrs = getBlockAttrs( listBlock.toString() );

  setAttributes({ showMetadata: parentBlockAttrs?.showMetadata });
  setAttributes({ allowedContributorRoles: parentBlockAttrs?.allowedContributorRoles });

  const isEmptyImage = isEmpty( uploadedImage );

  const blockProps = useBlockProps({
    className: `block__contributor flex`
  });

  /**
   * Image settings
   */

  const ALLOWED_MEDIA_TYPES = [ 'image' ];
  const onSelectUploadedImage = ( newUploadedImage ) => setAttributes({ uploadedImage: newUploadedImage });

  const onRemoveUploadedImage = () => {
    setAttributes({
      uploadedImage: undefined,
      imageSelection: undefined
    });
  };

  const uploadText = !isEmptyImage ? 'Replace image' : 'Upload image';
  const onChangeAltTextManual = ( newAltTextManual ) => setAttributes({ altTextManual: newAltTextManual });

  /**
   * API image settings
   */

  const handleAuthorId = ( newAuthorId ) => setAttributes({ authorId: newAuthorId });
  const [ data, setData ] = useState({});
  const [ isError, setError ] = useState( false );

  const onFetchApiImage = async() => {
    await apiFetch({
      path: '/dwt/v2/single-author',
      method: 'POST',
      data: { authorId }
    })
      .then( ( response ) => {
        // console.log( response.data );

        if ( isError ) {
          setError( false );
        }

        const clientAuthorsProp = response.data.clientAuthors[Object.keys( response.data.clientAuthors )[0]];
        return response.data.works.length === 0 && !isEmpty( clientAuthorsProp ) ? setData( clientAuthorsProp ) : setData( response.data );
      })
      .catch( ( error ) => {
        console.error( error );
        setError( true );
      });
  };

  const getApiImage = ( contributorData ) => {
    // Use author photo if available
    if ( contributorData.hasAuthorPhoto ) {
      return `https://images.penguinrandomhouse.com/author/${contributorData.authorId}`;
    }
    // Use first work cover if available
    const firstWorkCover = getFirstWork( contributorData );
    if ( firstWorkCover ) {
      return firstWorkCover;
    }
    // Use placeholder blur if no image available
    return 'https://images.penguinrandomhouse.com/cover/placeholder?blur_rad=0&blur_sig=20';
  };

  const getFirstWork = ( contributorData ) => {
    if ( isEmpty( contributorData ) ) return;
    if ( contributorData.works.length === 0 ) return;
    return `https://images.penguinrandomhouse.com/cover/${contributorData.works[0].isbn}?blur_rad=0&blur_sig=20`;
  };
  const onRemoveApiImage = () => {
    setAttributes({
      apiImage: undefined,
      authorId: undefined,
      uploadedImage: undefined
    });
  };

  useEffect( () => {
    if ( isEmpty( data ) ) {
      return;
    }

    const encodedDisplay = encodeURIComponent( data?.display );
    const encodedSpotlight = encodeURIComponent( data?.spotlight );

    setAttributes({
      hasAuthorPhoto: data?.hasAuthorPhoto,
      apiImage: getApiImage( data ),
      firstWorkCover: getFirstWork( data ),
      authorId: authorId?.toString(),
      authorRoles: getContributorRoles( data ),
      display: encodedDisplay,
      firstInitial: data?.firstInitial ?? '',
      lastInitial: data?.lastInitial ?? '',
      spotlight: encodedSpotlight,
      photocredit: data?.photoCredit,
      photoDate: data?.photoDate,
      altTextManual: data?.display
    });
  }, [ data ] );

  const getContributorRoles = ( contributorData ) => {
    const authors = contributorData.works?.map( ( work ) => work.authors );
    const matchAuthorId = authors?.map( ( author ) => author?.filter( ( item ) => item.authorId === contributorData.authorId ? item : null ) );
    const flatten = matchAuthorId?.flat();
    const roleCodes = flatten?.map( ( item ) => item.roleCode );
    const unique = [ ...new Set( roleCodes ) ];
    const arr = [];

    allowedContributorRoles.reduce( ( acc, item ) => {
      if ( unique.includes( item.value ) ) {
        acc.push( item.label );
      }
      return acc;
    }, [] );

    return arr;
  };

  /**
   * Handle additional logic
   */

  const inValidHelpText = isError ? 'Please enter a valid Contributor ID' : 'Required';
  const showPlaceholder = !!( isEmptyImage && !apiImage );

  const imageSrc = !isEmptyImage ? uploadedImage.sizes.full.url : apiImage;
  const imageAltText = altTextManual || ( altTextManual === '' || !altTextManual ? decodeURIComponent( display ) : uploadedImage.alt );
  const showAuthorInitialsOverlay = !hasAuthorPhoto && isEmptyImage;

  /**
   * Handle element styles
   */

  /**
   * Handle element classes
   */

  return (
    <>
      <InspectorControls>
        <PanelBody>
          { !apiImage && (
            <>
              <TextControl
                className={ isError ? 'has-error' : ''}
                label={ __( 'Contributor ID', 'dwt-custom-theme' ) }
                value= { authorId }
                onChange={ handleAuthorId }
                help={ __( inValidHelpText, 'dwt-custom-theme' ) }
              />
              <Button
                onClick={ onFetchApiImage }
                className='editor-media-placeholder__button is-button is-default is-large is-secondary rounded is-button-fetch'
              >
                { __( 'Fetch Contributor', 'dwt-custom-theme' ) }
              </Button>
            </>
          ) }
          { apiImage && (
            <>
              <div className="author-id mb-24">
                <h2>{ decodeURIComponent( display ) }</h2>
                <h2>{ `ID: ${authorId}` }</h2>
                <Button
                  onClick={ onRemoveApiImage }
                  isLink
                  isDestructive
                  className='flex mb-10'
                >
                  { __( 'Remove Contributor', 'dwt-custom-theme' ) }
                </Button>
              </div>

              <div className="components-base-control">
                <MediaUploadCheck>
                  <MediaUpload
                    allowedTypes={ ALLOWED_MEDIA_TYPES }
                    onSelect={ onSelectUploadedImage }
                    type='image'
                    value={ uploadedImage }
                    render={ ({ open }) => (
                      <Button
                        onClick={ open }
                        icon='upload'
                        className='editor-media-placeholder__button is-button is-default is-large is-secondary is-upload-button'
                      >
                        { __( uploadText, 'dwt-custom-theme' ) }
                      </Button>
                    ) }
                  />
                  <p className='text-12 text-gray mt-8'>Use an uploaded image</p>
                </MediaUploadCheck>
                { !isEmptyImage && (
                  <>
                    <Button
                      onClick={ onRemoveUploadedImage }
                      isLink
                      isDestructive
                      className='is-remove-button'
                    >
                      { __( 'Remove uploaded image', 'dwt-custom-theme' ) }
                    </Button>
                    <p className="text-12 text-gray">Removing uploaded image defaults back to image from the API</p>
                  </>
                ) }
              </div>
              <TextControl
                label={ __( 'Alt Text', 'dwt-custom-theme' ) }
                value= { altTextManual }
                onChange={ onChangeAltTextManual }
                help={ __( 'Add alt text', 'dwt-custom-theme' ) }
              />
            </>
          ) }
        </PanelBody>
      </InspectorControls>

      <div { ...blockProps }>
        <div className='contributor--inner'>
          { showPlaceholder && (

            <Placeholder
              title={ __( 'Image', 'dwt-custom-theme' ) }
              text={ __( 'Enter a contributor ID from the side panel.', 'dwt-custom-theme' ) }
              icon='format-image'
            />

          ) }

          { !showPlaceholder && (
            <>
              <div className="image-wrapper flex aspect-[81/100] overflow-hidden">
                <div className="api-image-wrapper flex relative w-full">
                  <img className='api-image' src={ imageSrc } alt={ imageAltText } data-authorid={ authorId } loading="lazy" />
                  { showAuthorInitialsOverlay && (
                    <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'>
                      { authorShapeIcon }
                      <span className='heading-1 m-0 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'>{ firstInitial + lastInitial }</span>
                    </div>
                  ) }
                </div>
              </div>

              { showMetadata && (
                <div className="contributor-details pt-16 w-full">
                  { display && (
                    <p className="contributor-details__title paragraph-1 text-16 my-0">{ decodeURIComponent( display ) }</p>
                  ) }

                  { authorRoles.length > 0 && (
                    <p className="contributor-details__title paragraph-2 text-[0.9rem] my-0">{ authorRoles.join( ', ' ) }</p>
                  ) }
                </div>
              ) }
            </>
          ) }

        </div>

      </div>
    </>
  );
};

export default Edit;
